import { Link } from "react-router-dom";
import { Container, SectionHeader, PullQuote, CTAStrip, ImageBlock } from "@/components/Sections";
import { SERVICES, RANGER_SUPPORT, IMAGES } from "@/lib/content";
import { ArrowRight } from "lucide-react";

export default function Services() {
  return (
    <div data-testid="page-services" className="bg-[#F6F1E6]">
      <section className="pt-40 md:pt-52 pb-24">
        <Container>
          <p className="overline mb-6">Section 06 — Services</p>
          <h1
            data-testid="services-title"
            className="display-xxl text-5xl md:text-7xl lg:text-[112px] mb-10"
          >
            Practical support for
            <br />
            <span className="italic text-[#3F5132]">people who care for Country.</span>
          </h1>
          <p className="text-[#4F5A48] text-lg md:text-xl max-w-3xl leading-relaxed">
            Four services. Built around the long arc of environmental workforce
            development — from initial skills assessment to operational monitoring
            systems delivered in the field.
          </p>
        </Container>
      </section>

      <section className="border-t border-[#1F2A1B]/10">
        <Container>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#1F2A1B]/10 my-0">
            {SERVICES.map((s) => (
              <div
                key={s.slug}
                data-testid={`service-${s.slug}`}
                className="bg-[#F6F1E6] p-10 md:p-14 lg:p-16"
              >
                <div className="flex items-baseline justify-between mb-10">
                  <span className="section-number">— Service {s.number}</span>
                  <span className="overline">{`0${SERVICES.indexOf(s) + 1}/0${SERVICES.length}`}</span>
                </div>
                <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.05] text-[#1F2A1B] mb-6">
                  {s.title}
                </h2>
                <p className="text-[#4F5A48] text-base md:text-lg leading-relaxed mb-10 max-w-md">
                  {s.summary}
                </p>
                <p className="overline mb-4">Services include</p>
                <ul className="space-y-0">
                  {s.items.map((item, i) => (
                    <li
                      key={item}
                      className="flex items-baseline gap-4 py-3 border-b border-[#1F2A1B]/10"
                    >
                      <span className="font-serif italic text-[#C45A3C] text-xs w-6 shrink-0">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="text-[#1F2A1B] text-base">{item}</span>
                    </li>
                  ))}
                </ul>
                {s.linkHref && (
                  <Link
                    to={s.linkHref}
                    data-testid={`service-${s.slug}-link`}
                    className="btn-ghost mt-10"
                  >
                    {s.linkLabel || "Learn more"}
                    <ArrowRight size={14} />
                  </Link>
                )}
              </div>
            ))}
          </div>
        </Container>
      </section>

      <section className="py-32 md:py-40 border-t border-[#1F2A1B]/10 bg-[#EBE3D0]">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
            <div className="lg:col-span-5">
              <SectionHeader
                number="Section 08"
                label="For Ranger Programs"
                title="Working alongside"
                italic="ranger programs."
              />
              <p className="text-[#4F5A48] text-lg leading-relaxed mb-8 max-w-xl">
                Every ranger program is different. Our role is not to replace
                existing training providers or cultural governance structures.
              </p>
              <p className="font-serif italic text-2xl md:text-3xl text-[#3F5132] leading-snug">
                Our role is to help identify emerging opportunities and create
                practical pathways into environmental professions.
              </p>
            </div>
            <div className="lg:col-span-7">
              <ImageBlock
                src={IMAGES.regrowth}
                alt="Green regrowth emerging from burnt forest"
                caption="Long-term capability"
                className="aspect-[16/10] mb-12"
                testid="rangers-image"
              />
              <p className="overline mb-4">Areas of support</p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-10">
                {RANGER_SUPPORT.map((r, i) => (
                  <li
                    key={r}
                    className="flex items-baseline gap-4 py-4 border-b border-[#1F2A1B]/10"
                  >
                    <span className="font-serif italic text-[#C45A3C] text-xs w-6 shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-[#1F2A1B] text-base md:text-lg">{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Container>
      </section>

      <Container>
        <PullQuote testid="services-quote">
          Building environmental infrastructure means investing in people,
          skills, monitoring systems, data and knowledge.
        </PullQuote>
      </Container>

      <CTAStrip
        heading="Let's design what comes next."
        body="From workforce planning to monitoring frameworks — we partner with organisations on the practical work of building environmental capability."
        label="Begin a project"
        testid="services-cta-strip"
      />
    </div>
  );
}
